import React from 'react';
import ReactHammer from 'react-hammerjs';

import { NavigationParamsContextProvider, ScreenWithOptionalParams, useTabNavigationContext } from 'core/navigation';
import {
  Case1BenefitsTabContent,
  Case2BenefitsTabContent,
  Case3BenefitsTabContent,
  Case1ChallengesTabContent,
  Case2ChallengesTabContent,
  Case3ChallengesTabContent,
  Case1OverviewTabContent,
  Case2OverviewTabContent,
  Case3OverviewTabContent,
} from '../../component';
import { CASE1_ROUTE, CASE2_ROUTE, CASE3_ROUTE } from '../Route.constant';
import classNames from './EABD2CaseNavigator.module.scss';

export const CaseNavigator = React.memo(() => {
  const { screens, selectedScreenIndex, select } = useTabNavigationContext();

  const swipeThresholdInPxRef = React.useRef<number>(100);

  const ScreenListStyles = React.useMemo<React.CSSProperties>(() => {
    const numberOfScreens = screens.length;
    const translateXBaseValueInPercent = -1 * (100 / numberOfScreens);
    const totalWidthInPercent = numberOfScreens * 100;

    const screenListStyles: React.CSSProperties = {
      transform: `translateX(${selectedScreenIndex * translateXBaseValueInPercent}%)`,
      width: `${totalWidthInPercent}%`,
    };

    return screenListStyles;
  }, [screens, selectedScreenIndex]);

  const OnSwipeCallback = React.useCallback(
    (event: HammerInput) => {
      const { deltaX } = event;

      if (Math.abs(deltaX) < swipeThresholdInPxRef.current) {
        return;
      }

      const canSwipeLeft = selectedScreenIndex < screens.length - 1;
      const canSwipeRight = selectedScreenIndex > 0;

      if (deltaX < 0 && canSwipeLeft) {
        select(selectedScreenIndex + 1);
        return;
      }

      if (deltaX > 0 && canSwipeRight) {
        select(selectedScreenIndex - 1);
        return;
      }
    },
    [selectedScreenIndex, screens, select]
  );

  const RenderScreenCallback = React.useCallback((screen: ScreenWithOptionalParams) => {
    switch (screen.screen) {
      case CASE1_ROUTE.OVERVIEW:
        return <Case1OverviewTabContent />;
      case CASE1_ROUTE.CHALLENGES:
        return <Case1ChallengesTabContent />;
      case CASE1_ROUTE.BENEFITS:
        return <Case1BenefitsTabContent />;
      case CASE2_ROUTE.OVERVIEW:
        return <Case2OverviewTabContent />;
      case CASE2_ROUTE.CHALLENGES:
        return <Case2ChallengesTabContent />;
      case CASE2_ROUTE.BENEFITS:
        return <Case2BenefitsTabContent />;
      case CASE3_ROUTE.OVERVIEW:
        return <Case3OverviewTabContent />;
      case CASE3_ROUTE.CHALLENGES:
        return <Case3ChallengesTabContent />;
      case CASE3_ROUTE.BENEFITS:
        return <Case3BenefitsTabContent />;
      default:
        throw new Error('CaseNavigator: unknown screen type!');
    }
  }, []);

  const RenderScreenItemCallback = React.useCallback(
    (screen: ScreenWithOptionalParams, index: number) => {
      return (
        <div key={`${screen.screen}-${index}`} className={classNames.screenListItem}>
          <NavigationParamsContextProvider params={screen.params}>{RenderScreenCallback(screen)}</NavigationParamsContextProvider>
        </div>
      );
    },
    [RenderScreenCallback]
  );

  return (
    <ReactHammer onSwipe={OnSwipeCallback}>
      <div className={classNames.root}>
        <div className={classNames.screenList} style={ScreenListStyles}>
          {screens.map(RenderScreenItemCallback)}
        </div>
      </div>
    </ReactHammer>
  );
});
